import type maplibregl from "maplibre-gl";
import { ROUTE_LINE_LAYOUT } from "../../shared/line-paint.ts";
import { sanitizeRouteCoordinates } from "../../shared/quest-feed.ts";
import { DEFAULT_QUEST_ROUTE_COLOR, type RouteFillFeature, type RoutePaintFeature } from "../../shared/quest-route-paint.ts";
import type { LngLat } from "../../shared/types.ts";
import { mapHasLayer, mapHasStyleObject } from "../../shared/map-style-safe.ts";
import { setStyledRouteData, styledRouteLayerIds } from "../quest/styled-route-layers.ts";

/** Home browse-mode layers: clustered quest start pins, preview route lines, and the selected quest's route. */
export const NEARBY_SOURCE = "nearby";
export const NEARBY_LAYER = "nearby-unclustered";
export const NEARBY_CLUSTER = "nearby-clusters";
const NEARBY_CLUSTER_COUNT = "nearby-cluster-count";
const CACHED_SOURCE = "cached-paths";
export const CACHED_LINE = "cached-lines";
/** Preview route lines show from this zoom up, under the open route. */
const CACHED_MIN_ZOOM = 13;
export const HOME_QUEST_ROUTE_IDS = styledRouteLayerIds("home-quest-route");

const PIN_COLOR = "#3a86ff";
const PIN_SELECTED_COLOR = "#ff6b4a";

export interface NearbyPin {
  id: string;
  title: string;
  at: LngLat;
}

export interface CachedRouteLine {
  id: string;
  title: string;
  path: LngLat[];
}

const emptyCollection = () => ({ type: "FeatureCollection" as const, features: [] });

export function ensureNearbyLayers(map: maplibregl.Map): void {
  if (!mapHasStyleObject(map) || map.getSource(NEARBY_SOURCE)) return;
  map.addSource(NEARBY_SOURCE, {
    type: "geojson",
    data: emptyCollection(),
    cluster: true,
    clusterMaxZoom: 14,
    clusterRadius: 56,
  });
  map.addLayer({
    id: NEARBY_CLUSTER,
    type: "circle",
    source: NEARBY_SOURCE,
    filter: ["has", "point_count"],
    paint: {
      "circle-color": ["step", ["get", "point_count"], "#ff6b4a", 10, "#ff4d6d", 30, "#e11d48"],
      "circle-radius": ["step", ["get", "point_count"], 16, 10, 20, 30, 26],
      "circle-stroke-color": "#ffffff",
      "circle-stroke-width": 2,
    },
  });
  map.addLayer({
    id: NEARBY_CLUSTER_COUNT,
    type: "symbol",
    source: NEARBY_SOURCE,
    filter: ["has", "point_count"],
    layout: {
      "text-field": ["to-string", ["get", "point_count_abbreviated"]],
      "text-size": 13,
      "text-font": ["Noto Sans Regular"],
      "text-allow-overlap": true,
    },
    paint: { "text-color": "#ffffff" },
  });
  map.addLayer({
    id: NEARBY_LAYER,
    type: "circle",
    source: NEARBY_SOURCE,
    filter: ["!", ["has", "point_count"]],
    paint: {
      "circle-radius": 7,
      "circle-color": PIN_COLOR,
      "circle-stroke-color": "#ffffff",
      "circle-stroke-width": 2,
    },
  });
}

export function ensureCachedRouteLines(map: maplibregl.Map, beforeId: string): void {
  if (!mapHasStyleObject(map) || map.getSource(CACHED_SOURCE)) return;
  map.addSource(CACHED_SOURCE, { type: "geojson", data: emptyCollection() });
  map.addLayer(
    {
      id: CACHED_LINE,
      type: "line",
      source: CACHED_SOURCE,
      minzoom: CACHED_MIN_ZOOM,
      layout: ROUTE_LINE_LAYOUT,
      paint: {
        "line-color": "#ff4d6d",
        "line-width": 3,
        "line-opacity": 0.45,
      },
    },
    beforeId,
  );
}

/** Clustered pins for the nearby list (at each quest's route bbox center). */
export function setNearbyPins(map: maplibregl.Map, pins: readonly NearbyPin[]): void {
  const src = map.getSource(NEARBY_SOURCE) as maplibregl.GeoJSONSource | undefined;
  src?.setData({
    type: "FeatureCollection",
    features: pins.map((p) => ({
      type: "Feature" as const,
      properties: { id: p.id, title: p.title },
      geometry: { type: "Point" as const, coordinates: p.at },
    })),
  });
}

export function setCachedRouteLines(map: maplibregl.Map, paths: readonly CachedRouteLine[]): void {
  const src = map.getSource(CACHED_SOURCE) as maplibregl.GeoJSONSource | undefined;
  if (!src) return;
  try {
    src.setData({
      type: "FeatureCollection",
      features: paths
        .map((p) => {
          const coordinates = sanitizeRouteCoordinates(p.path);
          if (coordinates.length < 2) return null;
          return {
            type: "Feature" as const,
            properties: { id: p.id, title: p.title },
            geometry: { type: "LineString" as const, coordinates },
          };
        })
        .filter((f): f is NonNullable<typeof f> => f != null),
    });
  } catch {
    /* never throw from a map data update — a bad list row must not freeze the renderer */
  }
}

/** Enlarge and recolor the selected quest's pin (null resets every pin). */
export function paintNearbyHighlight(map: maplibregl.Map, id: string | null): void {
  if (!mapHasLayer(map, NEARBY_LAYER)) return;
  try {
    const picked = ["==", ["get", "id"], id ?? ""];
    map.setPaintProperty(NEARBY_LAYER, "circle-radius", ["case", picked, 11, 7]);
    map.setPaintProperty(NEARBY_LAYER, "circle-color", ["case", picked, PIN_SELECTED_COLOR, PIN_COLOR]);
    map.setPaintProperty(NEARBY_LAYER, "circle-stroke-width", ["case", picked, 3, 2]);
  } catch {
    /* style swap or torn-down map mid-highlight */
  }
}

/** True when styled quest route layers exist and the line source has features. */
export function homeQuestRoutePainted(map: maplibregl.Map, ids = HOME_QUEST_ROUTE_IDS): boolean {
  if (!mapHasLayer(map, ids.solid)) return false;
  try {
    const src = map.getSource(ids.source) as maplibregl.GeoJSONSource | undefined;
    return (src?.serialize?.()?.data?.features?.length ?? 0) > 0;
  } catch {
    return false;
  }
}

/** Saved quest line/fill on the home map (same layers as the quest page). True when a route is drawn. */
export function setHomeQuestRoute(
  map: maplibregl.Map,
  line: LngLat[] | null,
  paint: readonly RoutePaintFeature[] | null,
  fill: readonly RouteFillFeature[] | null,
): boolean {
  if (!line) {
    setStyledRouteData(map, HOME_QUEST_ROUTE_IDS, null, null, DEFAULT_QUEST_ROUTE_COLOR, null);
    return false;
  }
  setStyledRouteData(map, HOME_QUEST_ROUTE_IDS, paint, line, DEFAULT_QUEST_ROUTE_COLOR, fill);
  return homeQuestRoutePainted(map);
}
